import { getPendingImagePreviewFilter, getPendingImageWarmthOverlay } from '~/components/Common/Images/cropUtils';

import type { MediaImage, RoomOption, RoomSelection } from '../types';
import { DEFAULT_ROOM_NAME, HERO_ROOM_NAME } from '../utils';

export interface CropArea {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface LocalImageEdit {
  brightness: number;
  contrast: number;
  crop: CropArea;
  cropMode: 'fixed' | 'freeform';
  filename: string;
  roomId?: string;
  roomName: string;
  title: string;
  warmth: number;
}

const warmthOverlaySx = {
  position: 'absolute',
  inset: 0,
  mixBlendMode: 'soft-light',
  pointerEvents: 'none',
};

export function buildImageFilter(edit?: LocalImageEdit) {
  return getPendingImagePreviewFilter({
    brightness: edit?.brightness ?? 0,
    contrast: edit?.contrast ?? 0,
    warmth: edit?.warmth ?? 0,
  });
}

export function getImageWarmthOverlaySx(edit?: LocalImageEdit) {
  const warmthOverlay = getPendingImageWarmthOverlay({
    brightness: edit?.brightness ?? 0,
    contrast: edit?.contrast ?? 0,
    warmth: edit?.warmth ?? 0,
  });

  return warmthOverlay.opacity
    ? {
        ...warmthOverlaySx,
        bgcolor: warmthOverlay.color,
        opacity: warmthOverlay.opacity,
      }
    : undefined;
}

export type CropHandle =
  | 'move'
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left';

export const MIN_CROP_SIZE = 18;

export function buildFilename(image: MediaImage) {
  const publicIdParts = image.publicId.split('/');
  const filename = publicIdParts[publicIdParts.length - 1];

  return filename.includes('.') ? filename : `${filename}.jpg`;
}

export function getDefaultRoomOption(image: MediaImage, roomOptions: Array<RoomOption>): RoomSelection {
  if (image.sourceRoomId) {
    const roomOption = roomOptions.find((option) => option.roomId === image.sourceRoomId);

    if (roomOption) {
      return roomOption;
    }
  }

  if (image.sourceLabel === HERO_ROOM_NAME) {
    return {
      roomName: HERO_ROOM_NAME,
    };
  }

  return {
    roomName: DEFAULT_ROOM_NAME,
  };
}

export function buildDefaultEdit(image: MediaImage, roomOptions: Array<RoomOption>): LocalImageEdit {
  const defaultRoomOption = getDefaultRoomOption(image, roomOptions);

  return {
    brightness: 0,
    contrast: 0,
    crop: {
      height: 100,
      width: 100,
      x: 0,
      y: 0,
    },
    cropMode: 'fixed',
    filename: buildFilename(image),
    roomId: defaultRoomOption.roomId,
    roomName: defaultRoomOption.roomName,
    title: image.title || '',
    warmth: 0,
  };
}

export function mergeEditWithDefaults(defaultEdit?: LocalImageEdit, edit?: LocalImageEdit) {
  if (!defaultEdit) {
    return undefined;
  }

  if (!edit) {
    return defaultEdit;
  }

  return {
    ...defaultEdit,
    ...edit,
    crop: edit.crop ?? defaultEdit.crop,
  };
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function getFixedCrop(crop: CropArea) {
  const size = clamp(Math.min(crop.width, crop.height), MIN_CROP_SIZE, 100);
  const centreX = crop.x + crop.width / 2;
  const centreY = crop.y + crop.height / 2;

  return {
    height: size,
    width: size,
    x: clamp(centreX - size / 2, 0, 100 - size),
    y: clamp(centreY - size / 2, 0, 100 - size),
  };
}

export function calculateCrop(
  handle: CropHandle,
  crop: CropArea,
  deltaX: number,
  deltaY: number,
  cropMode: LocalImageEdit['cropMode'],
) {
  if (handle === 'move') {
    return {
      ...crop,
      x: clamp(crop.x + deltaX, 0, 100 - crop.width),
      y: clamp(crop.y + deltaY, 0, 100 - crop.height),
    };
  }

  const right = crop.x + crop.width;
  const bottom = crop.y + crop.height;
  let nextLeft = crop.x;
  let nextTop = crop.y;
  let nextRight = right;
  let nextBottom = bottom;

  if (handle.includes('left')) {
    nextLeft = clamp(crop.x + deltaX, 0, right - MIN_CROP_SIZE);
  }

  if (handle.includes('right')) {
    nextRight = clamp(right + deltaX, crop.x + MIN_CROP_SIZE, 100);
  }

  if (handle.includes('top')) {
    nextTop = clamp(crop.y + deltaY, 0, bottom - MIN_CROP_SIZE);
  }

  if (handle.includes('bottom')) {
    nextBottom = clamp(bottom + deltaY, crop.y + MIN_CROP_SIZE, 100);
  }

  let nextWidth = nextRight - nextLeft;
  let nextHeight = nextBottom - nextTop;

  if (cropMode === 'fixed') {
    const fixedSize = clamp(Math.min(nextWidth, nextHeight), MIN_CROP_SIZE, 100);

    if (handle.includes('left')) {
      nextLeft = right - fixedSize;
    } else {
      nextRight = nextLeft + fixedSize;
    }

    if (handle.includes('top')) {
      nextTop = bottom - fixedSize;
    } else {
      nextBottom = nextTop + fixedSize;
    }

    if (nextLeft < 0) {
      nextRight -= nextLeft;
      nextLeft = 0;
    }

    if (nextTop < 0) {
      nextBottom -= nextTop;
      nextTop = 0;
    }

    if (nextRight > 100) {
      nextLeft -= nextRight - 100;
      nextRight = 100;
    }

    if (nextBottom > 100) {
      nextTop -= nextBottom - 100;
      nextBottom = 100;
    }

    nextWidth = nextRight - nextLeft;
    nextHeight = nextBottom - nextTop;
  }

  return {
    height: nextHeight,
    width: nextWidth,
    x: nextLeft,
    y: nextTop,
  };
}
